import CircularProgress from '@mui/material/CircularProgress'
import React, { useEffect, useMemo, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import { Box, Button, TextareaAutosize, useTheme } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CommentIcon from '@mui/icons-material/Comment'
import { IconButtonWithTooltip, useGlobalMessageHandler } from 'components'
import { commentService as usCommentService, type KwComment } from 'services/us/commentService'
import dayjs from 'dayjs'

interface CommentDialogProps {
  recordId: number
  recordType: string
  recordStateId?: number
  disableButton?: boolean
  commentService?: typeof usCommentService
  lastUpdated?: string | null
  onSaved?: (lastUpdated: string) => void
}

export const CommentDialog: React.FC<CommentDialogProps> = ({
  recordId,
  recordType,
  disableButton = false,
  commentService = usCommentService,
  lastUpdated,
  onSaved
}) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [comment, setComment] = useState<KwComment>({ text: '', last_updated: '', updated_by: '' })
  const [textLoaded, setTextLoaded] = useState(false)
  const [textFetchFailed, setTextFetchFailed] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const theme = useTheme()

  useEffect(() => {
    if (recordId === 0) return
    if (lastUpdated !== undefined) {
      setComment({ text: '', last_updated: lastUpdated ?? '', updated_by: '' })
      setTextLoaded(false)
      setTextFetchFailed(false)
      setLoading(false)
      return
    }
    setLoading(true)
    commentService.getComment(recordType, recordId)
      .then(comment => {
        setComment(comment)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [recordId, recordType, lastUpdated])

  useEffect(() => {
    if (recordId === 0) return
    if (lastUpdated === undefined) return
    if (!open || textLoaded) return
    setLoading(true)
    commentService.getComment(recordType, recordId)
      .then(comment => {
        setComment(comment)
        setTextLoaded(true)
        setTextFetchFailed(false)
        setLoading(false)
      })
      .catch((error) => {
        if (error?.response?.status !== 404) {
          showErrorMessage('Failed to load comment')
          setTextFetchFailed(true)
        }
        setLoading(false)
      })
  }, [open, lastUpdated, textLoaded, recordType, recordId, commentService])

  const buttonColor = useMemo(() => {
    if (comment.last_updated === '') return theme.palette.grey[400]
    const lastUpdated = dayjs(comment.last_updated)
    const twoWeeksAgo = dayjs().subtract(2, 'weeks')

    if (lastUpdated.isAfter(twoWeeksAgo)) {
      return theme.palette.primary.main
    } else {
      return theme.palette.primary.light
    }
  }, [comment.last_updated, comment.updated_by])

  const handleEditClick = (): void => {
    setDisabled(false)
  }

  const handleSaveClick = (): void => {
    clearMessages()
    setLoading(true)

    commentService.updateComment(recordType, recordId, { comment: comment.text })
      .then(() => {
        const savedLastUpdated = new Date().toISOString()
        showSuccessMessage('Comment saved successfully')
        setComment(prevState => { return { ...prevState, last_updated: savedLastUpdated } })
        onSaved?.(savedLastUpdated)
        setDisabled(true)
        setOpen(false)
      })
      .catch(() => {
        showErrorMessage('Failed to save comment')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  if (!open) {
    return (
      <IconButtonWithTooltip
        label="Comment"
        disabled={disableButton}
        onClick={() => { setOpen(true) }}
        sx={{
          color: (theme) => buttonColor
        }}
        icon={<CommentIcon/>}
      />
    )
  }

  return (
    <Dialog
      open={open}
      onClose={() => { setOpen(false) }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          width: '80vw'
        }
      }}
    >
      <DialogTitle>Comment</DialogTitle>
      <DialogContent>
        {loading
          ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
            )
          : (
          <TextareaAutosize
            value={comment.text}
            onChange={(e) => { setComment(prevState => { return { ...prevState, text: e.target.value } }) }}
            disabled={disabled || textFetchFailed}
            minRows={80}
            style={{ width: '100%' }}
          />
            )}
      </DialogContent>
      <DialogActions>
        {disabled
          ? (
          <IconButtonWithTooltip
            label="Edit"
            color="primary"
            disabled={textFetchFailed}
            onClick={handleEditClick}
            icon={<EditIcon/>}
          />
            )
          : (
          <IconButtonWithTooltip
            label="Save"
            color="primary"
            disabled={loading || textFetchFailed}
            onClick={handleSaveClick}
            icon={<SaveIcon/>}
          />
            )}
        <Button onClick={() => { setOpen(false) }} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
